import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import {
  useInvoiceDetail,
  useSendInvoice,
  useVoidInvoice,
  useDeleteInvoice,
  useUpdateInvoice,
} from '../api/queries/invoices';
import {
  useVoidInvoicePayment,
  useDeletePaymentImage,
} from '../api/queries/invoice-payments';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { invoiceLineType } from '../utils/invoice';
import { RecordPaymentModal } from '../components/RecordPaymentModal';
import { PaymentImageThumb } from '../components/PaymentImageThumb';
import { EditInvoiceModal } from '../components/EditInvoiceModal';
import { IconAction } from '../components/IconAction';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import './InvoiceDetailPage.css';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

const METHOD_LABEL: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  card: 'Card',
  other: 'Other',
};

import { formatMoney, formatDiscountPct, hasTax } from '../utils/money';
import { Toggle } from '../components/Toggle';

export function InvoiceDetailPage(): JSX.Element {
  const { id: idRaw } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNum = idRaw ? Number(idRaw) : NaN;
  const id = Number.isInteger(idNum) && idNum > 0 ? idNum : null;

  const { data, isPending, error } = useInvoiceDetail(id);
  const sendInvoice = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const deleteInvoice = useDeleteInvoice();
  const updateInvoice = useUpdateInvoice();
  const voidPayment = useVoidInvoicePayment();
  const deleteImage = useDeletePaymentImage();
  const [actionError, setActionError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  if (id === null) return <p className="id-status id-error">Invalid invoice id.</p>;
  if (isPending) return <p className="id-status">Loading…</p>;
  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return <p className="id-status id-error">Invoice not found.</p>;
    }
    if (error instanceof ApiError && error.status === 403) {
      return <p className="id-status id-error">You don't have permission to view this invoice.</p>;
    }
    return <p className="id-status id-error">Failed to load.</p>;
  }

  const inv = data!.invoice;
  const payments = inv.payments ?? [];
  const paidAmountNum = inv.paid_amount ? Number(inv.paid_amount) : 0;
  const balanceDueNum = Math.max(0, Math.round((Number(inv.total) - paidAmountNum) * 100) / 100);
  const balanceDue = balanceDueNum.toFixed(2);
  const isPartial = inv.status !== 'paid' && inv.status !== 'void' && paidAmountNum > 0;
  // Editing payments (record / void / images) is allowed until the invoice is
  // void; the Record button additionally needs a positive remaining balance.
  // Whether this invoice prints its per-line discount (migration 0053). `??`
  // rather than a bare read: an API older than 0053 omits the field, and the
  // column it governs was always shown.
  const showDiscount = inv.show_discount ?? true;
  const canEditPayments = can('invoice.mark_paid') && inv.status !== 'void';
  const canRecordPayment = canEditPayments && balanceDueNum > 0;
  const canSend = can('invoice.send') && inv.status === 'draft';
  const canEdit = can('invoice.write') && inv.status !== 'void';
  // Drafts are deleted (soft-delete); sent/overdue are voided; paid: neither.
  const canDelete = can('invoice.delete') && inv.status === 'draft';
  const canVoid = can('invoice.delete')
    && inv.status !== 'draft' && inv.status !== 'paid' && inv.status !== 'void';

  const onVoid = async () => {
    if (id === null) return;
    setActionError(null);
    if (!confirm('Void this invoice? Linked time entries will return to unbilled.')) return;
    try {
      await voidInvoice.mutateAsync(id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setActionError(body?.error ?? 'Cannot void in current state.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setActionError("You don't have permission to void invoices.");
        return;
      }
      setActionError(apiErrorText(err, 'Could not void invoice. Try again.'));
    }
  };

  const onVoidPayment = async (paymentId: number) => {
    if (id === null) return;
    setActionError(null);
    if (!confirm('Void this payment? The invoice balance will be recalculated.')) return;
    try {
      await voidPayment.mutateAsync({ invoice_id: id, payment_id: paymentId });
    } catch (err) {
      setActionError(apiErrorText(err, 'Could not void the payment. Try again.'));
    }
  };

  const onDeleteImage = async (paymentId: number, imageId: number) => {
    if (id === null) return;
    setActionError(null);
    if (!confirm('Remove this check image?')) return;
    try {
      await deleteImage.mutateAsync({ invoice_id: id, payment_id: paymentId, image_id: imageId });
    } catch (err) {
      setActionError(apiErrorText(err, 'Could not remove the image. Try again.'));
    }
  };

  const onDelete = async () => {
    if (id === null) return;
    setActionError(null);
    if (!confirm('Delete this draft invoice? Linked time entries will return to unbilled.')) return;
    try {
      await deleteInvoice.mutateAsync(id);
      navigate('/invoices');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setActionError(body?.error ?? 'Only draft invoices can be deleted.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setActionError("You don't have permission to delete invoices.");
        return;
      }
      setActionError(apiErrorText(err, 'Could not delete the invoice. Try again.'));
    }
  };

  const onSend = async () => {
    if (id === null) return;
    setActionError(null);
    try {
      await sendInvoice.mutateAsync(id);
      // Nothing was emailed — delivery is the operator's. Put the finished PDF
      // in front of them so marking it sent and having it to attach is one step.
      setPdfOpen(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError('Invoice is no longer in draft. Refresh to see current state.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setActionError("You don't have permission to mark invoices sent.");
        return;
      }
      setActionError(apiErrorText(err, 'Could not mark the invoice sent. Try again.'));
    }
  };

  return (
    <div className="id-page">
      <header className="id-header">
        <button className="btn-text" onClick={() => navigate('/invoices')}>
          ← All invoices
        </button>
        <h2>
          <code className="id-number">{inv.invoice_number}</code>
        </h2>
        <span className={`iv-pill iv-status-${inv.status}`}>
          {STATUS_LABEL[inv.status] ?? inv.status}
        </span>

        {/* One group, so a header too narrow for everything moves the actions
            down together instead of orphaning whichever happens to be last. */}
        <div className="id-header-actions">
          <button
            type="button"
            className="icon-btn id-pdf-btn"
            onClick={() => setPdfOpen(true)}
            title="Preview PDF"
            aria-label="Preview PDF"
          >
            <FileText size={18} strokeWidth={1.75} aria-hidden />
          </button>
          {canSend && (
            <button
              type="button"
              className="btn-charcoal-secondary id-action-btn"
              onClick={onSend}
              disabled={sendInvoice.isPending}
            >
              {sendInvoice.isPending ? 'Marking…' : 'Mark sent'}
            </button>
          )}
          {canRecordPayment && (
            <button
              type="button"
              className="btn-cyan-primary id-action-btn"
              onClick={() => setPayOpen(true)}
            >
              Record payment
            </button>
          )}
          {/* Any invoice, paid or void too — a copy is a new draft. */}
          {can('invoice.write') && (
            <IconAction action="duplicate" onClick={() => navigate(`/invoices/new?from=${inv.id}`)} />
          )}
          {canEdit && (
            <IconAction action="edit" onClick={() => setEditOpen(true)} />
          )}
          {canDelete && (
            <IconAction action="delete" onClick={onDelete} disabled={deleteInvoice.isPending} />
          )}
          {canVoid && (
            <button
              type="button"
              className="btn-charcoal-secondary id-action-btn id-void-btn"
              onClick={onVoid}
              disabled={voidInvoice.isPending}
            >
              {voidInvoice.isPending ? 'Voiding…' : 'Void'}
            </button>
          )}
        </div>
      </header>

      {actionError && (
        <p className="id-action-error" role="alert">{actionError}</p>
      )}

      <section className="id-meta">
        <div>
          <div className="id-meta-label">Bill to</div>
          <div className="id-meta-value">
            {inv.client_company_name ?? inv.client_name}
            {inv.client_company_name && inv.client_name && (
              <div className="id-meta-sub">{inv.client_name}</div>
            )}
          </div>
        </div>
        <div>
          <div className="id-meta-label">Issue date</div>
          <div className="id-meta-value">{fmtDate(inv.issue_date)}</div>
        </div>
        <div>
          <div className="id-meta-label">Due date</div>
          <div className="id-meta-value">{fmtDate(inv.due_date)}</div>
        </div>
        {/* The one job under the project this invoice bills (0061). */}
        {inv.matter_name && (
          <div>
            <div className="id-meta-label">Matter</div>
            <div className="id-meta-value">{inv.matter_name}</div>
          </div>
        )}
      </section>

      <section className="id-lines">
        {/* Flippable after the fact — deciding you'd rather not show the
            discount shouldn't mean voiding the invoice and rebuilding it. It
            governs the PDF too, so a re-download reflects the change. */}
        {can('invoice.write') && inv.status !== 'void' && (
          <Toggle
            checked={showDiscount}
            onChange={(v) =>
                updateInvoice.mutate({ id: inv.id, show_discount: v })}
            label="Show discount vs. standard rate (on this invoice and its PDF)"
            disabled={updateInvoice.isPending}
            className="id-disc-toggle"
          />
        )}
        <table className="id-line-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Type</th>
              <th className="id-numeric">Quantity</th>
              <th className="id-numeric">Unit</th>
              {showDiscount && <th className="id-numeric">Discount</th>}
              <th className="id-numeric">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.length === 0 ? (
              <tr>
                <td colSpan={showDiscount ? 7 : 6} className="id-empty-row">No line items.</td>
              </tr>
            ) : (
              inv.line_items.map((li, idx) => (
                <tr key={li.id}>
                  <td>{idx + 1}</td>
                  <td className="id-line-desc">{li.description}</td>
                  <td>
                    <span className={`id-line-type id-line-type-${invoiceLineType(li).toLowerCase()}`}>
                      {invoiceLineType(li)}
                    </span>
                  </td>
                  <td className="id-numeric">{Number(li.quantity).toFixed(2)}</td>
                  <td className="id-numeric">{formatMoney(li.unit_amount)}</td>
                  {showDiscount && (
                    <td className="id-numeric">
                      {formatDiscountPct(li.unit_amount, li.global_unit_amount) ?? '—'}
                    </td>
                  )}
                  <td className="id-numeric id-line-total">{formatMoney(li.line_total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="id-totals">
          <div className="id-total-row">
            <span>Subtotal</span>
            <span>{formatMoney(inv.subtotal)}</span>
          </div>
          {/* A zero tax gets no row at all — see hasTax(). */}
          {hasTax(inv.tax_amount) && (
            <div className="id-total-row">
              <span>Tax</span>
              <span>{formatMoney(inv.tax_amount)}</span>
            </div>
          )}
          <div className="id-total-row id-total-grand">
            <span>Total</span>
            <span>{formatMoney(inv.total)}</span>
          </div>
          {paidAmountNum > 0 && (
            <>
              <div className="id-total-row">
                <span>Amount paid</span>
                <span>{formatMoney(inv.paid_amount ?? '0')}</span>
              </div>
              {inv.status !== 'paid' && (
                <div className="id-total-row id-balance-row">
                  <span>Balance due</span>
                  <span>{formatMoney(balanceDue)}</span>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="id-payments">
        <div className="id-payments-head">
          <h3>Payment</h3>
          {inv.status === 'paid' ? (
            <span className="iv-pill iv-status-paid">Paid</span>
          ) : isPartial ? (
            <span className="iv-pill id-pill-partial">
              Partially paid · {formatMoney(balanceDue)} due
            </span>
          ) : (
            <span className="id-payments-unpaid">Balance due {formatMoney(balanceDue)}</span>
          )}
          {canRecordPayment && (
            <button
              type="button"
              className="btn-cyan-primary id-action-btn"
              onClick={() => setPayOpen(true)}
            >
              Record payment
            </button>
          )}
        </div>

        {payments.length === 0 ? (
          <p className="id-payments-empty">No payments recorded yet.</p>
        ) : (
          <ul className="id-payment-list">
            {payments.map((p) => (
              <li key={p.id} className="id-payment-row">
                <div className="id-payment-line">
                  <span className="id-payment-amount">{formatMoney(p.amount)}</span>
                  <span className="id-payment-date">{fmtDate(p.payment_date)}</span>
                  {p.method && (
                    <span className="id-payment-tag">{METHOD_LABEL[p.method] ?? p.method}</span>
                  )}
                  {p.bank_account_label && (
                    <span className="id-payment-bank">{p.bank_account_label}</span>
                  )}
                  {p.reference_number && (
                    <span className="id-payment-ref">#{p.reference_number}</span>
                  )}
                  {canEditPayments && (
                    <button
                      type="button"
                      className="btn-text id-payment-void"
                      onClick={() => onVoidPayment(p.id)}
                      disabled={voidPayment.isPending}
                    >
                      Void Payment
                    </button>
                  )}
                </div>
                {p.notes && <div className="id-payment-notes">{p.notes}</div>}
                {p.images.length > 0 && (
                  <div className="id-payment-images">
                    {p.images.map((img) => (
                      <PaymentImageThumb
                        key={img.id}
                        invoiceId={id}
                        paymentId={p.id}
                        imageId={img.id}
                        caption={img.caption}
                        canDelete={canEditPayments}
                        onDelete={() => onDeleteImage(p.id, img.id)}
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {inv.notes && (
        <section className="id-notes">
          <h3>Notes</h3>
          <p>{inv.notes}</p>
        </section>
      )}

      <RecordPaymentModal
        open={payOpen}
        invoiceId={id}
        clientId={inv.client_id}
        balanceDue={balanceDue}
        onClose={() => setPayOpen(false)}
      />

      <EditInvoiceModal
        open={editOpen}
        invoice={inv}
        onClose={() => setEditOpen(false)}
      />

      {pdfOpen && (
        <DocumentPreviewModal
          file={{
            downloadUrl: `/api/invoices/${id}/pdf`,
            filename: `${inv.invoice_number}.pdf`,
            mimeType: 'application/pdf',
          }}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}
